<template>
    <div>
        <h1 class="display-heading">Register A New {{accountType}} Account</h1>

        <div v-if="isInvitation" class="card mb-3">
            <div class="card-body">
                <p>Hi {{inviteeName ? inviteeName : 'User'}},</p>

                <p>Welcome to {{ companyName }}!</p>
                <p>You have been invited to join our platform! To continue, please complete the form below to create
                    your new account.</p>
            </div>

        </div>

        <div v-if="isSuperAdmin && isRegisterClinicAccountOnBehalf && registrationType == 5" class="mb-3">
            <button class="btn btn-primary" @click="showAdminMode">Admin Mode</button>
        </div>

        <div ref="formContainer" class="row">
            <div class="col-md-7">
                <register-user-component class="mb-5" v-model="form.user" ref="registerUserForm"
                    :emailReadonly="isInvitation" :emailAddressDisplay="emailAddressDisplay" :registrationType="registrationType"></register-user-component>

                <template v-if="registrationType == 2">
                    <register-doctor-component class="mb-5" v-model="form.doctor" ref="registerDoctorForm"
                        :emailReadonly="isInvitation" :emailAddressDisplay="emailAddressDisplay">
                    </register-doctor-component>
                </template>

                <template v-if="registrationType == 3">
                    <register-pharmacy-component class="mb-5" v-model="form.pharmacy" ref="registerPharmacyForm"
                        :emailReadonly="isInvitation" :emailAddressDisplay="emailAddressDisplay">
                    </register-pharmacy-component>
                </template>

                <template v-if="registrationType == 4">
                    <register-company-component class="mb-5" v-model="form.company" ref="registerCompanyForm"
                        :emailReadonly="isInvitation" :emailAddressDisplay="emailAddressDisplay">
                    </register-company-component>
                </template>

                <template v-if="registrationType == 5">
                    <register-clinic-component class="mb-5" v-model="form.clinic" ref="registerClinicForm">
                    </register-clinic-component>
                </template>

                <div>
                    <div class="mb-3">
                        <div>
                            <a :href="termsAndConditionsUrl" target="_blank">Click here to view our Terms and Conditions</a>
                        </div>
                        <div v-for="(doc, index) in additionalDocuments" :key="index">
                            <a :href="doc.url" target="_blank">Click here to view {{ doc.name }}</a>
                        </div>
                    </div>
                    <p>
                        Please read the attached documents carefully before accessing, using or obtaining any materials, information or services. By accessing {{ companyName }} Platform (collectively "Our Website") you agree to be bound by these <a :href="termsAndConditionsUrl" target="_blank">Terms and Conditions</a>
                        <span v-for="(doc, index) in additionalDocuments" :key="index">
                            <span v-if="index + 1 === additionalDocuments.length"> and </span>
                            <span v-else>, </span>
                            <a :href="doc.url" target="_blank">{{ doc.name }}</a>
                        </span>.
                    </p>

                    <div class="mb-3">
                        <div class="form-check">
                            <label class="form-check-label d-flex">
                                <input class="form-check-input" type="checkbox" v-model="tncAndPrivacyPolicyAccepted">
                                <div class="ml-2" style="margin-top:-3px">I hereby acknowledge that I have read, understand and agree to the Terms and Conditions
                                    <span v-for="(doc, index) in additionalDocuments" :key="index">
                                        <span v-if="index + 1 === additionalDocuments.length"> and </span>
                                        <span v-else>, </span>
                                        {{ doc.name }}
                                    </span>
                                    of {{ companyName }} Platform.
                                </div>
                                    </label>
                        </div>
                    </div>

                    <p v-if="formHasError" class="text-danger">
                        {{formErrorMessage}}
                    </p>

                    <button type="button" class="btn btn-primary btn-primary-insv mr-2" @click="registerNow">
                        Register Now
                    </button>

                    <router-link class="btn btn-secondary" :to="{'name': 'login'}">Cancel</router-link>
                </div>
            </div>
        </div>

        <popup-modal ref="popupModalError" :hasCloseButton="false" title="Invalid Invitation Details">
            <template slot="content">
                <p>{{invitationError}}</p>
                <p>If you believe that this is an error, please <a :href="`mailto:${companyCustomerSupportEmail}`">contact
                        our support team</a>.</p>
            </template>
            <template slot="footer">
                <button @click="backToLogin" class="btn btn-primary btn-primary-insv">Back to Login</button>
            </template>
        </popup-modal>

        <popup-modal ref="popupRegistrationError" :hasCloseButton="false" title="Error Creating New User">
            <template slot="content">
                <p>{{createUser.errorMessage}}</p>
                <ul>
                    <li v-for="(error, key) in createUser.errors" :key="key">
                        {{error}}
                    </li>
                </ul>
            </template>
            <template slot="footer">
                <button @click="dismissCreateError" class="btn btn-primary btn-primary-insv">Try Again</button>
            </template>
        </popup-modal>

        <popup-modal ref="popupModalSuccess" :hasCloseButton="false" :title="registerSuccess ? 'Registration Complete!' : 'Registering Your New Account'">
            <template slot="content">
                <template v-if="registerSuccess">
                    <!-- teledoctor account -->
                    <template v-if="registrationType===2">
                        <div>
                            <p>Thanks, your clinic application is being processed. You will receive our decision via email soon.</p>
                        </div>
                    </template>
                    <!-- corporate account -->
                    <template v-else-if="registrationType===4">
                        <div>
                            <p>
                                Your new corporate account has been created!
                            </p>
                            <p>
                                Please check the registered email address's inbox, spam or other folders, and open our welcoming email to confirm registered email address.
                            </p>
                            <p>
                                Within this welcoming email, there are user guide and video link for corporate users to learn the key functions of our system.
                            </p>
                        </div>
                    </template>
                    <!-- clinic account -->
                    <template v-else-if="registrationType===5">
                        <div v-if="isRegisterClinicAccountOnBehalf">
                            <p>New clinic account created! Your information is saved and encrypted.</p>
                            <p>
                                Please email and request doctor acceptance of our terms and conditions. Upon receiving the signed scanned copy from doctor, you may approve the panel clinic via "Clinic List" module.
                            </p>
                        </div>
                        <div v-else>
                            <p>Thanks, your clinic application is being processed. You will receive our decision via email soon.</p>
                        </div>
                    </template>
                    <!-- other types of users -->
                    <template v-else>
                        <div>
                            <p>Your new account has been created! Your information is saved and encrypted.</p>

                            <template v-if="userEmailVerificationRequired">
                                <p>A confirmation email has been sent to your email address. Please click on the confirmation link in the email to activate your new account.</p>
                            </template>
                            <template v-else>
                                <p>Please login to continue.</p>
                            </template>
                        </div>
                    </template>
                </template>
                <template v-else>
                    <p>Please wait while we are processing your registration...</p>
                </template>
            </template>
            <template v-if="registerSuccess" slot="footer">
                <button @click="loginToContinue" class="btn btn-primary btn-primary-insv">
                    <template v-if="registrationType===2 || registrationType==5">
                        Okay
                    </template>
                    <template v-else>
                        {{ userEmailVerificationRequired ? 'Go to Login Screen' : 'Login to My New Account' }}
                    </template>
                </button>
            </template>
        </popup-modal>

        <popup-modal ref="popupAdminMode" :hasCloseButton="true" title="Admin Mode">
            <template slot="content">
                <p>Please paste the exported data in the box below:</p>
                <textarea v-model="adminModeTextarea" rows="5" class="form-control"></textarea>
            </template>
            <template slot="footer">
                <button @click="applyAdminMode" class="btn btn-primary btn-primary-insv">Apply</button>
            </template>
        </popup-modal>
    </div>

</template>

<script>
import {
    convertDateToIso8601
} from '@/utilities';
import companyTypes, { isCompanySponsoredType } from '@/config/companyTypes';
import popupModal from '@/components/popup-modal';
import RegisterUserComponent from '@/components/register/user';
import RegisterDoctorComponent from '@/components/register/doctor';
import RegisterPharmacyComponent from '@/components/register/pharmacy';
import RegisterCompanyComponent from '@/components/register/company';
import RegisterClinicComponent from '@/components/register/clinic';
import Cookies from 'js-cookie';

export default {
    'name': 'Register',
    'components': {
        RegisterDoctorComponent,
        RegisterPharmacyComponent,
        RegisterCompanyComponent,
        RegisterUserComponent,
        RegisterClinicComponent,
        popupModal
    },
    beforeMount () {
        if (this.$route.query.c && this.$route.query.t && typeof parseInt(this.$route.query.t) === 'number') {
            this.form.registrationCode = this.$route.query.c;

            this.registrationType = [1, 2, 3, 4, 5].includes(parseInt(this.$route.query.t)) ? parseInt(this.$route.query.t) : 1;
        } else if (typeof parseInt(this.$route.query.t) === 'number') {
            if (parseInt(this.$route.query.t) === 2) {
                this.registrationType = 2;
            } else if (parseInt(this.$route.query.t) === 4) {
                this.registrationType = 4;
            } else if (parseInt(this.$route.query.t) === 5) {
                this.registrationType = 5;
            }
        }
    },
    mounted () {
        if (this.registrationType === 4) {
            const companyTypesValues = this.companyTypes.map((ct) => ct.value);
            if (typeof parseInt(this.$route.query.ct) === 'number' && !isNaN(parseInt(this.$route.query.ct)) && companyTypesValues.includes(parseInt(this.$route.query.ct))) {
                this.form.company.companyType = parseInt(this.$route.query.ct);
            } else {
                let query = {
                    'accountType': 1
                };

                if (this.form.registrationCode) {
                    query.c = this.form.registrationCode;
                }

                this.$router.push({
                    'name': 'register-landing',
                    query
                });
            }
        }
        // #region from invitation email
        if (this.form.registrationCode) {
            this.axios.get('Invite/GetInviteeDetails', {
                'params': {
                    'type': this.registrationType,
                    'code': this.form.registrationCode
                }
            }).then(({
                data
            }) => {
                const responseData = data.data;
                if (responseData) {
                    if (responseData.iv) {
                        this.inviteeName = responseData.n;
                        this.emailAddressDisplay = responseData.e;

                        // this.form.user.name = responseData.n;
                        // this.form.user.mobileNumber = responseData.pn;
                        // this.form.user.identificationType = responseData.it;
                        // this.form.user.identificationNumber = responseData.ic;
                        // this.form.user.email = responseData.e;

                        this.isInvitation = true;
                    } else {
                        if (data.data.iu) {
                            this.invitationError =
                                    'Invalid invitation details. You have previously registered your account.';
                        } else {
                            this.invitationError = 'Invalid invitation details.';
                        }

                        this.$refs.popupModalError.show();
                    }
                }
            });
        }
        // #endregion
        // #region superadmin register clinic
        if (this.isRegisterClinicAccountOnBehalf) {
            this.registrationType = 5;
        }
        // #endregion

        // #region referralCode
        if (Cookies.get('referralCode')) {
            this.form.user.referralCode = Cookies.get('referralCode');
        }

        if (this.$route.query) {
            if (
                this.$route.query.r !== undefined) {
                Cookies.set('referralCode', this.$route.query.r);
                this.form.user.referralCode = this.$route.query.r;
            }
        }
        // #endregion
    },
    'computed': {
        companyName () {
            return this.$store.state.operator.companyNameDisplay;
        },
        companyCustomerSupportEmail () {
            return this.$store.state.operator.supportEmailAddress;
        },
        termsAndConditionsUrl () {
            switch (this.registrationType) {
            case 2:// tele-doctors
                return this.$store.state.operator.termsAndConditionDoctorsUrl;
            case 5: // panel clinics
                return this.$store.state.operator.clinic_general_terms_of_use_url;
            default:
                return this.$store.state.operator.termsAndConditionPatientsUrl;
            }
        },
        additionalDocuments () {
            switch (this.registrationType) {
            case 4:// company account
                return [
                    {
                        'name': 'Employer Consent of Data Sharing Letter',
                        'url': this.$store.state.operator.employer_consent_of_data_sharing_letter_url
                    }
                ];
            case 5: // panel clinics
                return [
                    {
                        'name': 'Additional Terms of Use',
                        'url': this.$store.state.operator.terms_and_conditions_panel_clinic_url
                    },
                    {
                        'name': 'Letter of Undertaking',
                        'url': this.$store.state.operator.letter_of_undertaking_panel_clinic_url
                    }
                ];
            default:
                return [
                    {
                        'name': 'Privacy Policy',
                        'url': this.$store.state.operator.privacyPolicyUrl
                    }
                ];
            }
        },
        accountType () {
            switch (this.registrationType) {
            case 2:
                return 'Doctor';

            case 3: return 'Pharmacy';

            case 4: return 'Company';
            case 5: return 'Clinic';
            case 6: return 'Clinic Doctor';

            case 7: return 'Third Party Administrator';

            default:
                return 'User';
            }
        },
        isRegisterClinicAccountOnBehalf () {
            return this.$route.name === 'register-clinic';
        },
        isSuperAdmin () {
            return this.$store.state.user.userRole === 8;
        }
    },
    data () {
        return {
            companyTypes,
            'isInvitation': false,
            'emailAddressDisplay': null,
            'registrationType': 1,
            'invitationError': null,
            'inviteeName': null,
            'form': {
                'registrationCode': '',
                'doctor': {
                    'affiliation': '',
                    'yearsPracticing': 0,
                    'education': '',
                    'mmcNumber': '',
                    'apcNumber': '',
                    // 'borangBNumber': '',
                    'apcExpiry': '',
                    'location': '',
                    'languagesSpeak': [],
                    'specialtyIds': [],
                    'bankAccountPayeeName': '',
                    'bankName': '',
                    'bankAccountNumber': ''
                },
                'company': {
                    'companyName': '',
                    'companyRegistrationId': '',
                    'contactPerson': '',
                    'emailAddress': '',
                    'phoneNumber': '',
                    'addressLine1': '',
                    'addressLine2': '',
                    'addressCity': '',
                    'addressState': '',
                    'addressPostcode': '',
                    'companyType': -1,
                    'benefitCommencementDate': null,
                    'benefitRefreshDate': null
                },
                'pharmacy': {
                    'companyName': '',
                    'companyRegistrationId': '',
                    'contactPerson': '',
                    'emailAddress': '',
                    'phoneNumber': '',
                    'addressLine1': '',
                    'addressLine2': '',
                    'addressCity': '',
                    'addressState': '',
                    'addressPostcode': '',
                    'bankName': '',
                    'bankAccountNumber': ''
                },
                'user': {
                    'designation': '',
                    'designationChoice': 'Mr.',
                    'name': '',
                    'mobileNumber': '',
                    'identificationType': '',
                    'identificationNumber': '',
                    'email': '',
                    'password': '',
                    'repeatPassword': '',
                    'firstSixDigitCardNumber': '',

                    'referralCode': ''
                },
                'clinic': {
                    'name': '',
                    'clinicPhone': '',
                    'clinicEmail': '',
                    'languagesSpeak': [],
                    'clinicRegistrationNumber': '',
                    'gstRegistrationNumber': '',
                    'lat': '',
                    'long': '',
                    'fax': '',
                    'website': '',
                    'addressLine1': '',
                    'addressLine2': '',
                    'addressCity': '',
                    'addressState': '',
                    'addressPoscode': '',
                    'bankAccountPayeeName': '',
                    'bankName': '',
                    'bankAccountNumber': '',
                    'contactPerson': '',
                    'contactPersonDesignation': '',
                    'internetProvider': '',
                    'workingDays': [
                        {
                            'workingDayId': 1,
                            'startTime': '09:00 AM',
                            'endTime': '05:00 PM',
                            'text': 'Monday',
                            'selected': false
                        },
                        {
                            'workingDayId': 2,
                            'startTime': '09:00 AM',
                            'endTime': '05:00 PM',
                            'text': 'Tuesday',
                            'selected': false
                        },
                        {
                            'workingDayId': 3,
                            'startTime': '09:00 AM',
                            'endTime': '05:00 PM',
                            'text': 'Wednesday',
                            'selected': false
                        },
                        {
                            'workingDayId': 4,
                            'startTime': '09:00 AM',
                            'endTime': '05:00 PM',
                            'text': 'Thursday',
                            'selected': false
                        },
                        {
                            'workingDayId': 5,
                            'startTime': '09:00 AM',
                            'endTime': '05:00 PM',
                            'text': 'Friday',
                            'selected': false
                        },
                        {
                            'workingDayId': 6,
                            'startTime': '09:00 AM',
                            'endTime': '05:00 PM',
                            'text': 'Saturday',
                            'selected': false
                        },
                        {
                            'workingDayId': 7,
                            'startTime': '09:00 AM',
                            'endTime': '05:00 PM',
                            'text': 'Sunday',
                            'selected': false
                        }
                    ],
                    'operatingHoursRemarks': '',
                    'facilitiesAndServices': [],
                    'otherFacilitiesAndServices': '',
                    'borangbNumber': '',
                    'doctorMmcNumber': '',
                    'doctorApcNumber': ''
                }
            },
            'createUser': {
                'errorMessage': '',
                'errors': []
            },
            'tncAndPrivacyPolicyAccepted': false,
            'formHasError': false,
            'formErrorMessage': '',
            'registerSuccess': false,
            'userEmailVerificationRequired': false,
            'adminModeTextarea': null
        };
    },
    'methods': {
        backToLogin () {
            this.$refs.popupModalError.hide().then(() => {
                this.$router.push('login');
            });
        },
        formatCommaPayload (array) {
            let string = '';
            for (const [index, element] of array.entries()) {
                string = string + element;

                if (index !== array.length - 1) {
                    string = string + ',';
                }
            }

            return string;
        },
        registerNow () {
            switch (this.registrationType) {
            case 2: {
                Promise.all([this.$refs.registerUserForm.validate(), this.$refs.registerDoctorForm.validate()])
                    .then(
                        (validationResults) => {
                            this.$nextTick(() => {
                                if (validationResults.filter(v => v === false).length > 0 || !this.tncAndPrivacyPolicyAccepted) {
                                    let errorFields = this.$refs.formContainer.querySelectorAll(
                                        '.is-invalid');

                                    this.formHasError = true;

                                    if (errorFields.length > 0) {
                                        errorFields[0].focus();

                                        this.formErrorMessage = `There's error(s) in the form. Please check the form and try again.`;
                                    } else {
                                        this.formErrorMessage = `Please confirm that you have read and understood all of our Terms and Conditions and Privacy Policy before continue.`;
                                    }
                                } else {
                                    this.$refs.popupModalSuccess.show(() => {
                                        this.registerSuccess = false;

                                        this.formHasError = false;
                                        let formData = this.createUserDataForm();
                                        formData.append('RegistrationNumber', this.form.doctor.mmcNumber);
                                        formData.append('Apcnumber', this.form.doctor.apcNumber);
                                        // formData.append('Apcexpiry', convertDateToIso8601(this.form.doctor
                                        //     .apcExpiry));
                                        // formData.append('BorangBNumber', this.form.doctor.borangBNumber);
                                        formData.append('Education', this.form.doctor.education);
                                        formData.append('Location', this.form.doctor.location);
                                        formData.append('YearsPracticing', this.form.doctor
                                            .yearsPracticing);
                                        formData.append('HospitalOrClinicAffiliations', this.form.doctor
                                            .affiliation);
                                        formData.append('LanguagesSpeak', this.formatCommaPayload(this.form.doctor.languagesSpeak));
                                        formData.append('BankAccountPayeeName', this.form.doctor.bankAccountPayeeName);
                                        formData.append('BankName', this.form.doctor.bankName);
                                        formData.append('BankAccountNumber', this.form.doctor.bankAccountNumber);

                                        this.form.doctor.specialtyIds.forEach(specialtyId => {
                                            formData.append('Specialties', specialtyId);
                                        });
                                        let profilePictureFile = document.querySelector(
                                            '#uploadProfilePhotoInput');
                                        formData.append('ProfilePhotoFile', profilePictureFile.files[0]);

                                        let apcFile = document.querySelector('#uploadAPCFileInput');
                                        if (apcFile.files[0]) {
                                            formData.append('APCFile', apcFile.files[0]);
                                        }

                                        let mmcFile = document.querySelector('#uploadMMCFileInput');
                                        if (mmcFile.files[0]) {
                                            formData.append('MMCFile', mmcFile.files[0]);
                                        }

                                        // let borangBFile = document.querySelector('#uploadBorangBFileInput');
                                        // formData.append('BorangBFile', borangBFile.files[0]);

                                        this.axios.post('Account/RegisterDoctor', formData, {
                                            'headers': {
                                                'Content-Type': 'multipart/form-data'
                                            }
                                        }).then(({
                                            data
                                        }) => {
                                            this.registerSuccess = true;
                                        }).catch((error) => {
                                            if (error && error.response && error.response.data) {
                                                this.createUser.errorMessage = error.response.data
                                                    .message;
                                                let errorList = this.spreadRegistrationErrors(error
                                                    .response.data.data && error.response.data
                                                    .data.ve ? error.response.data.data.ve : []);

                                                this.createUser.errors = errorList;
                                            } else {
                                                this.createUser.errorMessage =
                                                        'An error has occured. Please try again.';
                                            }

                                            this.$refs.popupModalSuccess.hide().then(() => {
                                                this.$refs.popupRegistrationError.show();
                                            });
                                        });
                                    });
                                }
                            });
                        });
                break;
            }
            case 3: {
                Promise.all([this.$refs.registerUserForm.validate(), this.$refs.registerPharmacyForm
                    .validate()
                ]).then(
                    (validationResults) => {
                        this.$nextTick(() => {
                            if (validationResults.filter(v => v === false).length > 0 || !this.tncAndPrivacyPolicyAccepted) {
                                this.formHasError = true;

                                let errorFields = this.$refs.formContainer.querySelectorAll(
                                    '.is-invalid');

                                if (errorFields.length > 0) {
                                    errorFields[0].focus();
                                    this.formErrorMessage = `There's error(s) in the form. Please check the form and try again.`;
                                } else {
                                    this.formErrorMessage = `Please confirm that you have read and understood all of our Terms and Conditions and Privacy Policy before continue.`;
                                }
                            } else {
                                this.$refs.popupModalSuccess.show(() => {
                                    this.registerSuccess = false;

                                    this.formHasError = false;

                                    let formData = this.createUserDataForm();
                                    formData.append('CompanyName', this.form.pharmacy.companyName);
                                    formData.append('CompanyRegistrationNumber', this.form.pharmacy
                                        .companyRegistrationId);
                                    formData.append('ContactPersonName', this.form.pharmacy
                                        .contactPerson);
                                    formData.append('ContactPersonEmailAddress', this.form.pharmacy
                                        .emailAddress);
                                    formData.append('ContactPersonPhoneNumber', this.form.pharmacy
                                        .phoneNumber);
                                    formData.append('AddressLine1', this.form.pharmacy.addressLine1);
                                    formData.append('AddressLine2', this.form.pharmacy.addressLine2);
                                    formData.append('AddressCity', this.form.pharmacy.addressCity);
                                    formData.append('AddressState', this.form.pharmacy.addressState);
                                    formData.append('AddressPoscode', this.form.pharmacy
                                        .addressPostcode);
                                    formData.append('BankName', this.form.pharmacy.bankName);
                                    formData.append('BankAccountNumber', this.form.pharmacy.bankAccountNumber);

                                    this.axios.post('Account/RegisterPharmacy', formData).then(({
                                        data
                                    }) => {
                                        this.registerSuccess = true;
                                    }).catch((error) => {
                                        if (error && error.response && error.response.data) {
                                            this.createUser.errorMessage = error.response.data
                                                .message;
                                            let errorList = this.spreadRegistrationErrors(error
                                                .response.data.data && error.response.data
                                                .data.ve ? error.response.data.data.ve : []);

                                            this.createUser.errors = errorList;
                                        } else {
                                            this.createUser.errorMessage =
                                                    'An error has occured. Please try again.';
                                        }

                                        this.$refs.popupModalSuccess.hide().then(() => {
                                            this.$refs.popupRegistrationError.show();
                                        });
                                    });
                                });
                            }
                        });
                    });
                break;
            }
            case 4: {
                Promise.all([this.$refs.registerUserForm.validate(), this.$refs.registerCompanyForm.validate()])
                    .then(
                        (validationResults) => {
                            this.$nextTick(() => {
                                if (validationResults.filter(v => v === false).length > 0 || !this.tncAndPrivacyPolicyAccepted) {
                                    this.formHasError = true;

                                    let errorFields = this.$refs.formContainer.querySelectorAll(
                                        '.is-invalid');

                                    if (errorFields.length > 0) {
                                        errorFields[0].focus();
                                        this.formErrorMessage = `There's error(s) in the form. Please check the form and try again.`;
                                    } else {
                                        this.formErrorMessage = `Please confirm that you have read and understood all of our Terms and Conditions and Privacy Policy before continue.`;
                                    }
                                } else {
                                    this.$refs.popupModalSuccess.show(() => {
                                        this.registerSuccess = false;

                                        this.formHasError = false;
                                        let formData = this.createUserDataForm();
                                        formData.append('ReferralCode', this.form.user.referralCode);
                                        formData.append('CompanyName', this.form.company.companyName);
                                        formData.append('CompanyRegistrationNumber', this.form.company
                                            .companyRegistrationId);
                                        formData.append('ContactPersonName', this.form.company
                                            .contactPerson);
                                        formData.append('ContactPersonEmailAddress', this.form.company
                                            .emailAddress);
                                        formData.append('ContactPersonPhoneNumber', this.form.company
                                            .phoneNumber);
                                        formData.append('AddressLine1', this.form.company.addressLine1);
                                        formData.append('AddressLine2', this.form.company.addressLine2);
                                        formData.append('AddressCity', this.form.company.addressCity);
                                        formData.append('AddressState', this.form.company.addressState);
                                        formData.append('AddressPoscode', this.form.company
                                            .addressPostcode);
                                        formData.append('CompanyType', this.form.company.companyType);

                                        if (isCompanySponsoredType(this.form.company.companyType)) {
                                            formData.append('BenefitCommencementDate', convertDateToIso8601(this.form.company.benefitCommencementDate));
                                            formData.append('BenefitRefreshDate', convertDateToIso8601(this.form.company.benefitRefreshDate));
                                        }

                                        this.axios.post('Account/RegisterCompany', formData).then(({
                                            data
                                        }) => {
                                            this.registerSuccess = true;

                                            if (data.data && data.data.emailVerificationNeeded) {
                                                this.userEmailVerificationRequired = true;
                                            }
                                        }).catch((error) => {
                                            if (error && error.response && error.response.data) {
                                                this.createUser.errorMessage = error.response.data
                                                    .message;
                                                let errorList = this.spreadRegistrationErrors(error
                                                    .response.data.data && error.response.data
                                                    .data.ve ? error.response.data.data.ve : []);

                                                this.createUser.errors = errorList;
                                            } else {
                                                this.createUser.errorMessage =
                                                        'An error has occured. Please try again.';
                                            }

                                            this.$refs.popupModalSuccess.hide().then(() => {
                                                this.$refs.popupRegistrationError.show();
                                            });
                                        });
                                    });
                                }
                            });
                        });
                break;
            }
            case 5: {
                Promise.all([this.$refs.registerUserForm.validate(), this.$refs.registerClinicForm.validate()])
                    .then(validationResults => {
                        this.$nextTick(() => {
                            if (validationResults.filter(v => v === false).length > 0 || !this.tncAndPrivacyPolicyAccepted) {
                                let errorFields = this.$refs.formContainer.querySelectorAll(
                                    '.is-invalid');

                                this.formHasError = true;

                                if (errorFields.length > 0) {
                                    errorFields[0].focus();

                                    this.formErrorMessage = `There's error(s) in the form. Please check the form and try again.`;
                                } else {
                                    this.formErrorMessage = `Please confirm that you have read and understood all of our Terms and Conditions and Privacy Policy before continue.`;
                                }
                            } else {
                                this.$refs.popupModalSuccess.show(() => {
                                    this.registerSuccess = false;

                                    this.formHasError = false;
                                    let formData = new FormData();

                                    let facilitiesAndServices = '';

                                    if (this.form.clinic.facilitiesAndServices.length !== 0) {
                                        for (const [index, facilitiyAndService] of this.form.clinic.facilitiesAndServices.entries()) {
                                            facilitiesAndServices = facilitiesAndServices + facilitiyAndService;

                                            if (index !== this.form.clinic.facilitiesAndServices.length - 1) {
                                                facilitiesAndServices = facilitiesAndServices + ',';
                                            }
                                        }
                                        if (this.form.clinic.otherFacilitiesAndServices) {
                                            facilitiesAndServices = facilitiesAndServices + ',' + this.form.clinic.otherFacilitiesAndServices;
                                        }
                                    } else {
                                        if (this.form.clinic.otherFacilitiesAndServices) {
                                            facilitiesAndServices = this.form.clinic.otherFacilitiesAndServices;
                                        }
                                    }

                                    formData.append('InvitationCode', this.form.registrationCode);
                                    formData.append('InvitationType', this.registrationType);
                                    formData.append('Name', this.form.clinic.name);
                                    formData.append('ClinicPhone', this.form.clinic.clinicPhone);
                                    formData.append('ClinicEmail', this.form.clinic.clinicEmail);
                                    formData.append('MobileNumber', this.form.user.mobileNumber);
                                    formData.append('EmailAddress', this.isInvitation ? this.emailAddressDisplay : this.form.user.email);
                                    formData.append('Password', this.form.user.password);
                                    formData.append('LanguagesSpeak', this.formatCommaPayload(this.form.clinic.languagesSpeak));
                                    formData.append('Lat', this.form.clinic.lat);
                                    formData.append('Long', this.form.clinic.long);
                                    formData.append('Fax', this.form.clinic.fax);
                                    formData.append('Website', this.form.clinic.website);
                                    formData.append('AddressLine1', this.form.clinic.addressLine1);
                                    formData.append('AddressLine2', this.form.clinic.addressLine2);
                                    formData.append('AddressCity', this.form.clinic.addressCity);
                                    formData.append('AddressState', this.form.clinic.addressState);
                                    formData.append('AddressPoscode', this.form.clinic.addressPoscode);
                                    formData.append('BankAccountPayeeName', this.form.clinic.bankAccountPayeeName);
                                    formData.append('BankAccountNumber', this.form.clinic.bankAccountNumber);
                                    formData.append('BankName', this.form.clinic.bankName);
                                    formData.append('ContactPerson', this.form.clinic.contactPerson);
                                    formData.append('ContactPersonDesignation', this.form.clinic.contactPersonDesignation);
                                    formData.append('InternetProvider', this.form.clinic.internetProvider);
                                    formData.append('WorkingDays', JSON.stringify(this.form.clinic.workingDays.filter(workingDay => workingDay.selected === true)));
                                    formData.append('FacilitiesAndServices', facilitiesAndServices);
                                    formData.append('OperatingHoursRemarks', this.form.clinic.operatingHoursRemarks);

                                    // #region Clinic SSM
                                    formData.append('ClinicRegistrationNumber', this.form.clinic.clinicRegistrationNumber);
                                    if (this.form.clinic.clinicRegistrationNumber) {
                                        let ssmFileInput = document.querySelector('#ssmFileInput');
                                        if (ssmFileInput.files[0]) {
                                            formData.append('ClinicRegistrationFile', ssmFileInput.files[0]);
                                        }
                                    }
                                    // #endregion Clinic SSM

                                    // formData.append('GstRegistrationNumber', this.form.clinic.gstRegistrationNumber);

                                    // #region BorangBF
                                    formData.append('BorangbNumber', this.form.clinic.borangbNumber);

                                    let borangBFFileInput = document.querySelector('#borangBFFileInput');
                                    if (borangBFFileInput.files[0]) {
                                        formData.append('BorangbFile', borangBFFileInput.files[0]);
                                    }
                                    // #endregion BorangBF

                                    // #region DoctorMmcNumber
                                    formData.append('DoctorMmcNumber', this.form.clinic.doctorMmcNumber);

                                    let doctorMMCFileInput = document.querySelector('#doctorMMCFileInput');
                                    if (doctorMMCFileInput.files[0]) {
                                        formData.append('DoctorMmcFile', doctorMMCFileInput.files[0]);
                                    }
                                    // #endregion DoctorMmcNumber

                                    // #region DoctorApcNumber
                                    formData.append('DoctorApcNumber', this.form.clinic.doctorApcNumber);

                                    let doctorAPCFileInput = document.querySelector('#doctorAPCFileInput');
                                    if (doctorAPCFileInput.files[0]) {
                                        formData.append('DoctorApcFile', doctorAPCFileInput.files[0]);
                                    }
                                    // #endregion

                                    this.axios.post('Account/RegisterClinic', formData, {
                                        'headers': {
                                            'Content-Type': 'multipart/form-data'
                                        }
                                    }).then(({
                                        data
                                    }) => {
                                        this.registerSuccess = true;
                                    }).catch((error) => {
                                        if (error && error.response && error.response.data) {
                                            this.createUser.errorMessage = error.response.data
                                                .message;
                                            let errorList = this.spreadRegistrationErrors(error
                                                .response.data.data && error.response.data
                                                .data.ve ? error.response.data.data.ve : []);

                                            this.createUser.errors = errorList;
                                        } else {
                                            this.createUser.errorMessage =
                                                        'An error has occured. Please try again.';
                                        }

                                        this.$refs.popupModalSuccess.hide().then(() => {
                                            this.$refs.popupRegistrationError.show();
                                        });
                                    });
                                });
                            }
                        });
                    });
                break;
            }
            default: {
                this.$refs.registerUserForm.validate().then((isValid) => {
                    if (isValid && this.tncAndPrivacyPolicyAccepted) {
                        this.$refs.popupModalSuccess.show().then(() => {
                            this.registerSuccess = false;
                            this.formHasError = false;

                            let formData = this.createUserDataForm();
                            formData.append('FirstSixDigitsAlliancebankCardNumber', this.form.user.firstSixDigitCardNumber);
                            formData.append('ReferralCode', this.form.user.referralCode);
                            this.axios.post('Account/RegisterUser', formData).then(({
                                data
                            }) => {
                                this.registerSuccess = true;
                                if (data.data && data.data.emailVerificationNeeded) {
                                    this.userEmailVerificationRequired = true;
                                }
                            }).catch((error) => {
                                if (error && error.response && error.response.data) {
                                    this.createUser.errorMessage = error.response.data.message;
                                    let errorList = this.spreadRegistrationErrors(error.response
                                        .data.data && error.response.data.data.ve ? error
                                            .response.data.data.ve : []);

                                    this.createUser.errors = errorList;
                                } else {
                                    this.createUser.errorMessage =
                                            'An error has occured. Please try again.';
                                }
                                this.$refs.popupModalSuccess.hide().then(() => {
                                    this.$refs.popupRegistrationError.show();
                                });
                            });
                        });
                    } else {
                        this.formHasError = true;

                        let errorFields = this.$refs.formContainer.querySelectorAll(
                            '.is-invalid');

                        if (errorFields.length > 0) {
                            errorFields[0].focus();
                            this.formErrorMessage = `There's error(s) in the form. Please check the form and try again.`;
                        } else {
                            this.formErrorMessage = `Please confirm that you have read and understood all of our Terms and Conditions and Privacy Policy before continue.`;
                        }
                    }
                });

                break;
            }
            }
        },
        createUserDataForm () {
            let formData = new FormData();

            if (this.form.user.designationChoice !== -1) {
                this.form.user.designation = this.form.user.designationChoice;
            }

            formData.append('InvitationCode', this.form.registrationCode);
            formData.append('InvitationType', this.registrationType);
            formData.append('Designation', this.form.user.designation);
            formData.append('Name', this.form.user.name);
            formData.append('MobileNumber', this.form.user.mobileNumber);
            formData.append('IdentityType', this.form.user.identificationType);
            formData.append('IdentificationNumber', this.form.user.identificationNumber);
            formData.append('EmailAddress', this.isInvitation ? this.emailAddressDisplay : this.form.user.email);
            formData.append('Password', this.form.user.password);
            return formData;
        },
        dismissCreateError () {
            this.$refs.popupRegistrationError.hide();
        },
        spreadRegistrationErrors (errors) {
            let errorList = [];

            let firstLayerErrors = errors.map(e => e.e);

            firstLayerErrors.forEach(err => {
                if (err.length > 0) {
                    errorList.push(...err);
                }
            });

            return errorList;
        },
        loginToContinue () {
            this.$refs.popupModalSuccess.hide().then(() => {
                if (this.isRegisterClinicAccountOnBehalf && this.registrationType === 5) {
                    this.$router.push({
                        'name': 'view-clinics'
                    });
                } else {
                    this.$router.push({
                        'name': 'login'
                    });
                }
            });
        },
        showAdminMode () {
            this.$refs.popupAdminMode.show();
        },
        applyAdminMode () {
            this.$refs.popupAdminMode.hide().then(() => {
                if (this.adminModeTextarea) {
                    const data = JSON.parse(this.adminModeTextarea);

                    this.form.user.password = 'fpmpam2022';
                    this.form.user.repeatPassword = 'fpmpam2022';

                    if (data.title) {
                        this.form.clinic.contactPersonDesignation = data.title;
                    }

                    if (data.doctorName) {
                        this.form.clinic.contactPerson = data.doctorName;
                    }

                    if (data.phoneNumber) {
                        this.form.clinic.clinicPhone = data.phoneNumber;
                        this.form.user.mobileNumber = data.phoneNumber;
                    }

                    if (data.email) {
                        this.form.clinic.clinicEmail = data.email;
                        this.form.user.email = data.email;
                    }

                    if (data.clinicAddressLine1) {
                        this.form.clinic.name = data.clinicAddressLine1;
                    }

                    if (data.clinicAddressLine2) {
                        this.form.clinic.addressLine1 = data.clinicAddressLine2;
                    }

                    if (data.clinicAddressLine3) {
                        this.form.clinic.addressLine2 = data.clinicAddressLine3;
                    }

                    if (data.clinicAddressLine4) {
                        this.form.clinic.addressCity = data.clinicAddressLine4;
                    }

                    if (data.clinicAddressLine5) {
                        this.form.clinic.addressPoscode = data.clinicAddressLine5;
                    }

                    if (data.clinicAddressLine6) {
                        this.form.clinic.addressState = data.clinicAddressLine6;
                    }

                    this.form.clinic.clinicRegistrationNumber = '-';
                    this.form.clinic.borangbNumber = '-';
                    this.form.clinic.doctorMmcNumber = '-';
                    this.form.clinic.doctorApcNumber = '-';
                    this.form.clinic.languagesSpeak = ['English'];
                }
            });
        }
    }
};

</script>

<style>

</style>
